/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { HeaderContext } from '@/components/content/Header';
import { HeaderNavBarMenuList } from '@/components/content/Header/parts/NavBarMenuList';
import { headerMegaMenuDropMenuSX } from '@/components/content/Header/styles/navBar/dropMenu';
import { headerNavBarItemSX } from '@/components/content/Header/styles/navBar/item';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useContentRecommendation } from '@/data/Content/ContentRecommendation';
import { PageLink } from '@/data/Navigation';
import { Switch } from '@/utils/switch';
import { Box, Button, ClickAwayListener, Stack, Tooltip, useTheme } from '@mui/material';
import React, { useCallback, useContext, useEffect } from 'react';

type Props = {
	tree: PageLink[];
	label: string;
};

export const HeaderNavBarDropMenu = React.forwardRef(
	({ tree, label, ...props }: Props, ref: React.Ref<HTMLAnchorElement> | null) => {
		const theme = useTheme();
		const headerContext = useContext(HeaderContext);
		const router = useNextRouter();
		const open = headerContext.megaMenuOpen || false;
		const setOpen = headerContext.setMegaMenuOpen;
		const handleToolTip = useCallback(
			(action?: string) => () =>
				setOpen((open: boolean) =>
					Switch(action)
						.case('open', () => true)
						.case('close', () => false)
						.defaultTo(() => !open)
				),
			[setOpen]
		);
		useEffect(() => {
			if (!router.asPath) return;
			handleToolTip('close')();
		}, [handleToolTip, router.asPath]);
		useContentRecommendation('MegaMenu_1'); // preload content of mega menu as the button renders
		return (
			<ClickAwayListener onClickAway={handleToolTip('close')}>
				<Stack onMouseLeave={handleToolTip('close')}>
					<Tooltip
						placement="bottom-start"
						open={open}
						onClose={handleToolTip('close')}
						disableFocusListener
						disableHoverListener
						disableTouchListener
						componentsProps={{
							tooltip: {
								sx: headerMegaMenuDropMenuSX(theme, headerContext.onVideo) as any,
							},
						}}
						title={
							tree && tree.length > 0 ? (
								<Stack direction="row">
									<HeaderNavBarMenuList tree={tree} level={1} />
								</Stack>
							) : null
						}
					>
						<Box onMouseEnter={handleToolTip('open')} onTouchStart={handleToolTip('close')}>
							<Button component="a" ref={ref} {...props} sx={headerNavBarItemSX}>
								{label}
							</Button>
						</Box>
					</Tooltip>
				</Stack>
			</ClickAwayListener>
		);
	}
);
